import {AfterViewInit, Component, Input, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {AppService} from "../../app.service";
import {ApiRequestService} from "../../shared/api-request.service";
import {UtilsService} from "../../shared/utils.service";
import {tap} from "rxjs/operators";
import {ApiDataSource} from "../../utils/api-data-source";
import {AssetModel} from "../../models/asset.model";
import {IncidentsTabsComponent} from "../../incidents/incidents-tabs/incidents-tabs.component";
import {IncidentsSelectorComponent} from "../../shared/incidents-selector/incidents-selector.component";
import {RBACService} from "../../shared/rbac.service";
import {
  IncidentsSelectorFilterComponent
} from "../../shared/incidents-selector-filter/incidents-selector-filter.component";

@Component({
  selector: 'app-assets-view-incidents',
  templateUrl: './assets-view-incidents.component.html',
  styleUrls: ['./assets-view-incidents.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AssetsViewIncidentsComponent implements OnInit, AfterViewInit {

  @Input('asset_id') asset_id: number;
  @Input('asset') asset: AssetModel;

  dataSource: AssetsViewIncidentsDataSource = new AssetsViewIncidentsDataSource(this.app, this.api);

  // Built dynamically in ngOnInit(). The checkbox column only drives Unlink Selected, so it is
  // added for a user who holds the asset edit permission and omitted otherwise.
  listColumns: string[] = [];

  // the paginator and sorter
  @ViewChild('paginator1') paginator1: MatPaginator;
  @ViewChild('paginator2') paginator2: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  // RBAC permission flag for the link and unlink actions. Managing an asset's related records is
  // part of the asset workflow, so it is governed by the asset edit permission rather than by the
  // permissions of the related feature. Resolved once in ngOnInit().
  canEdit: boolean = false;

  // Opening the incident record itself belongs to the Incidents feature, so it carries that
  // feature's view permission instead. Resolved once in ngOnInit().
  canViewIncidents: boolean = false;

  constructor(
    private app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public rbac: RBACService
  ) { }

  ngOnInit() {
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_ASSETS, RBACService.RBAC_A.A_EDIT),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_ASSETS, RBACService.RBAC_A.A_EDIT)
    ], false);

    // Opening the incident itself is an INCIDENTS operation, not an asset one: the dialog loads the
    // incident from the Incidents API, which gates on that feature. Linking and unlinking stay on the
    // asset edit permission above, because those write the asset's own pivot.
    this.canViewIncidents = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_REGISTERS_INCIDENTS, RBACService.RBAC_A.A_VIEW),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_INCIDENTS, RBACService.RBAC_A.A_VIEW)
    ], false);

    this.listColumns = [];
    if (this.canEdit) {
      this.listColumns.push('select');
    }
    this.listColumns.push(
      'id',
      'details',
      'type',
      'status',
      'site',
      'date_incident',
      'actions'
    );

    this.dataSource.asset_id = this.asset_id;
    this.dataSource.getData();
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting takes place
    this.sort.sortChange.subscribe(() => {
      this.paginator1.pageIndex = 0;
      this.paginator2.pageIndex = 0;
    });

    const paginatorTap = tap((paginator) => {
      this.paginator1.pageIndex = paginator['pageIndex'];
      this.paginator1.pageSize = paginator['pageSize'];
      this.paginator2.pageIndex = paginator['pageIndex'];
      this.paginator2.pageSize = paginator['pageSize'];

      this.dataSource.limit = paginator['pageSize'];
      this.dataSource.offset = paginator['pageIndex'];
      this.dataSource.getData();
    });

    // Subscribe to the paginator tap events.
    this.paginator1.page.pipe(paginatorTap).subscribe();
    this.paginator2.page.pipe(paginatorTap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange.pipe(tap((sorter) => {
      this.dataSource.order_by = sorter['active'];
      this.dataSource.order = sorter['direction'];

      this.dataSource.getData();
    })).subscribe();
  }

  /**
   * Open the filter dialog and apply the selected filters.
   *
   * @return {void}
   */
  onFilter(): void {
    this.utils.showComponentDialog(
      IncidentsSelectorFilterComponent,
      {
        type: this.dataSource.type,
        status: this.dataSource.status,
        site_ids: this.dataSource.site_ids,
        user_ids: this.dataSource.user_ids,
        asset_ids: this.dataSource.asset_ids,
        date_range: this.dataSource.date_range,
        hide_assets: this.dataSource.hide_assets
      },
      {
        width: '900px'
      },
      (results) => {
        if (typeof results === 'undefined') { return; }

        this.dataSource.type = results.type ?? '';
        this.dataSource.status = results.status ?? '';
        this.dataSource.site_ids = results.site_ids ?? [];
        this.dataSource.user_ids = results.user_ids ?? [];
        this.dataSource.asset_ids = results.asset_ids ?? [];
        this.dataSource.date_range = results.date_range ?? [];

        this.dataSource.getData(true);
      }
    );
  }

  /**
   * Display a dialog to link/unlink incidents to the asset.
   *
   * @return {void}
   */
  onManage(): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to manage the incidents linked to this asset.');
      return;
    }

    this.utils.showComponentDialog(
      IncidentsSelectorComponent,
      {
        selected: this.dataSource.allIds,
        multiple: true,
        hide_assets: this.dataSource.hide_assets
      },
      {
        width: '1200px'
      },
      (results) => {
        if (typeof results != 'undefined') {
          // Set the newly selected ids.
          this.dataSource.allIds = results;

          // Send API request to sync incidents.
          this.api.makeRequest('put', `v2/assets/${this.asset_id}/incidents`, {
            ids: this.dataSource.allIds
          })
            .then(() => {
              // Refresh the list.
              this.dataSource.getData();
            })
            .catch((errorResponse) => {
              this.utils.handleAPIErrors(errorResponse);
            });
        }
      }
    );
  }

  /**
   * Unlinks the selected incident.
   *
   * @param {number} id - The ID of the incident to be unlinked.
   * @return {void}
   */
  onUnlink(id: number): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to unlink incidents from this asset.');
      return;
    }

    this.utils.showModal(
      'Unlink Selected Incident',
      'Are you sure you want to unlink the selected Incident?',
      () => {
        this.unlink(id, 'Incident was unlinked');
      });
  }

  /**
   * Unlinks the selected incidents.
   *
   * @return {void}
   */
  onUnlinkSelected(): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to unlink incidents from this asset.');
      return;
    }

    this.utils.showModal(
      'Unlink Selected Incidents',
      'Are you sure you want to unlink the selected Incidents?',
      () => {
        this.unlink(this.dataSource.selection.selected.join(','), 'Incidents were unlinked');
      });
  }

  /**
   * Unlinks incidents with the specified IDs.
   *
   * @param {any} ids - The incident IDs to be unlinked.
   * @param msg
   * @return {void}
   */
  private unlink(ids: any, msg: string): void {
    this.api.makeRequest('delete', `v2/assets/${this.asset_id}/incidents`, {},
      {
        incident_ids: ids
      })
      .then(() => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Manage the associated incident.
   *
   * @param {number} incident_id - The ID of the incident to manage.
   * @return {void}
   */
  onManageIncident(incident_id: number): void {
    if (!this.canViewIncidents) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view incidents.');
      return;
    }

    this.utils.showComponentDialog(
      IncidentsTabsComponent,
      {
        incident_id: incident_id
      },
      {
        width: '90%'
      },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData();
      }
    );
  }
}

export class AssetsViewIncidentsDataSource extends ApiDataSource {
  order_by = 'date_incident';
  order = 'desc';
  asset_id: number;

  // Filter options (mirrors the main Incidents list).
  type: string = '';
  status: string = '';
  site_ids: number[] = [];
  user_ids: number[] = [];
  asset_ids: number[] = [];
  date_range: Date[] = [];
  hide_assets: boolean = true;

  getData(resetOffset: boolean = false) {
    this.makeRequest(`v2/assets/${this.asset_id}/incidents`, resetOffset, {
      type: this.type || '',
      status: this.status || '',
      site_ids: this.site_ids.length ? this.site_ids.join(',') : '',
      user_ids: this.user_ids.length ? this.user_ids.join(',') : '',
      asset_ids: this.asset_ids.length ? this.asset_ids.join(',') : '',
      date_range: this.date_range && this.date_range.length > 1 ? [
        this.date_range[0].getTime() / 1000,
        this.date_range[1].getTime() / 1000
      ].join(',') : ''
    });
  }
}
